import * as React from 'react';
import { FieldValues } from 'react-hook-form';
import { Box } from '@mui/system';
import useMultiStep, { FormStep } from './useMultiStep';
import MultiStepFormHeader from './MultiStepFormHeader';
import { MultiStepContextProps } from './types';
import MultiStepFormFooter from './MultiStepFormFooter';
import { ModelForm } from '../../../forms/types';

export type MultiStepFormProps = {
    steps: FormStep<MultiStepContextProps>[];
    initiaValues: FieldValues;
    submitAction: (fieldValues: FieldValues) => void;
    isCreate?: boolean;
    /** Action d'annulation (navigation) ; à défaut le footer ferme la modale. */
    onCancel?: () => void;
    /** Notifie la fiche parente du libellé courant pour l'afficher dans l'en-tête en création. */
    onLabelChange?: (value: string) => void;
} & Pick<ModelForm<unknown>, 'afterSubmit' | 'isCreate'>;

const MultiStepForm: React.FunctionComponent<MultiStepFormProps> = function ({
    steps,
    initiaValues,
    submitAction,
    isCreate = true,
    onCancel,
    onLabelChange,
}) {
    const multiStepForm = useMultiStep(steps, initiaValues, submitAction);
    const {
        currentStep: { step },
    } = multiStepForm;

    const { component: CurrentStep } = step;
    const multiStepProps = {
        steps,
        multiStepForm,
        submitAction,
        isCreate,
        onCancel,
        onLabelChange,
    };

    // Validations transverses (évaluées sur l'ensemble des valeurs accumulées) :
    // un message par onglet en erreur, et un blocage global de l'enregistrement.
    // L'onglet courant est ignoré ici : sa validité est déjà gérée par `isStepValid`
    // (son `formContext` est monté), et ses valeurs ne sont pas encore persistées
    // dans `formValues` tant qu'on ne l'a pas quitté.
    const currentIndex = multiStepForm.currentStep.index;
    const stepErrors = steps.map((formStep, stepIndex) =>
        stepIndex === currentIndex ? undefined : formStep.validate?.(multiStepForm.formValues),
    );
    const blockingErrorMessage = stepErrors.find((error) => error !== undefined);
    const hasBlockingError = blockingErrorMessage !== undefined;

    return (
        <Box
            width="100%"
            minHeight="60vh"
            display="flex"
            flexDirection="column"
        >
            <MultiStepFormHeader
                {...multiStepProps}
                stepErrors={stepErrors}
            />
            <CurrentStep {...multiStepProps} />
            <MultiStepFormFooter
                {...multiStepProps}
                hasBlockingError={hasBlockingError}
                blockingErrorMessage={blockingErrorMessage}
            />
        </Box>
    );
};

export default MultiStepForm;
